/**
 * iconDescriptions.js — Single source of truth de descripciones de iconos IC.
 *
 * Exporta:
 *   - ICON_DESCRIPTIONS: { [iconName]: { title, body } } — 40 entradas
 *   - ICON_GROUPS: array de 9 grupos con metadata para renderizar pills
 *   - PILL_COLORS: paleta Tailwind light + dark
 *
 * Invariante de integridad: flatten(ICON_GROUPS.icons) === keys(ICON_DESCRIPTIONS).
 * Validada con console.assert al cargar el modulo (development only).
 *
 * Spec: docs/superpowers/specs/2026-05-19-icon-tooltips-pills-flag-dropdown.md
 */

export const ICON_DESCRIPTIONS = {
  // Cliente cerrado (sin pill)
  money: { title: 'Ya pagó', body: 'Cliente abonó o pagó el total de la compra.' },

  // Compra en Club Almacén (pill verde) — escala de decisión: firme → muy indeciso
  soccer:     { title: 'Va a comprar a Club Almacén', body: 'Dio señales claras de querer comprar a nosotros.' },
  basketball: { title: 'Quería comprarnos',           body: 'Pero muestra interés intermitente.' },
  rugby:      { title: 'Quería comprarnos',           body: 'Pero ahora su interés no es claro.' },
  badminton:  { title: 'Quería comprarnos',           body: 'Pero su interés se esfumó, no sabemos por qué.' },

  // SERCOTEC (pill ámbar) — ambas estrellas se llaman SERCOTEC; el body distingue
  sercotec_plus: { title: 'SERCOTEC', body: 'Está postulando o ya ganó el concurso y le interesa nuestro software.' },
  sercotec:      { title: 'SERCOTEC', body: 'Ya no muestra tanto interés o perdió el concurso.' },

  // Va a comprar software (pill amarillo) — escala de convencimiento: alto → bajo
  cake_full: { title: '¡Felicidades! Va a comprar un software. ¿A quién?', body: 'Mostró señales que comprará algún software pero aún no ha decidido cuál.' },
  cake:      { title: 'Va a comprar un software. ¿Cuál?',                  body: 'Su interés por nuestro software no es tan firme como antes.' },
  coffee:    { title: 'Va a comprar un software',                          body: 'Postergó la decisión, hay que esperar con calma.' },

  // Interesado (pill rojo)
  diamond_alarm:   { title: 'Interesado apurado',         body: 'Hay que ser muy diligente con él y no atrasarse.' },
  diamond_advisor: { title: 'Interesado busca asesor',    body: 'Conviene darle asistencia personalizada.' },
  diamond_return:  { title: 'Interesado que volvió',      body: 'Nos contactó antes, se fue, buscó en otro lado y no encontró — ahora vuelve.' },
  diamond_plus:    { title: 'Interesado entusiasmado',    body: 'Busca información y muestra entusiasmo.' },
  diamond_info:    { title: 'Interesado en información',  body: 'Solo pide información.' },
  diamond_price:   { title: 'Interesado solo en precio',  body: 'Su única pregunta es el precio.' },
  diamond:         { title: 'Interesado',                 body: 'Genérico — muestra interés pero sin matiz específico.' },

  // Frío / bajo interés (sin pill)
  diamond_blue: { title: 'Poco interés o perdió interés',             body: 'Nació frío o se enfrió.' },
  stopwatch:    { title: 'Diamante reagendó interés hacia el futuro', body: 'Dijo que retomaría en una fecha o mes futuro.' },
  hourglass:    { title: 'Diamante postergó decisión',                body: 'No sabemos para cuándo.' },
  cloud_rain:   { title: 'Diamante se nubló',                         body: 'Algo pasó y su interés no es claro.' },
  cloud_heavy:  { title: 'Diamante se mojó',                          body: 'Su interés se aguó. Algo pasó.' },
  cloud_storm:  { title: 'Diamante se quemó',                         body: 'Algo pasó. La oportunidad está casi perdida.' },
  rejected:     { title: 'Descartado',                                body: 'Ya compró · Nos falta una función · No tiene dinero · etc.' },

  // Marketing / Campañas (pill naranjo)
  publicidad_software:    { title: 'Campaña de software', body: 'Incluir en próximo envío de marketing sobre software.' },
  publicidad_kits:        { title: 'Campaña de Kits',     body: 'Incluir en próximo envío de marketing sobre kits.' },
  publicidad_bole:        { title: 'Campaña de Boletas',  body: 'Incluir en próximo envío de marketing sobre el módulo de boletas.' },
  publicidad_presupuesto: { title: 'Campaña de Precios',  body: 'Incluir en próximo envío con oferta o ajuste de precios.' },

  // Publicidad por segmento (pill naranjo, agrupados en fila Marketing / Campañas)
  publicidad_pelotas:    { title: 'Campaña Club Almacén', body: 'Incluir en el próximo envío de marketing dirigido a clientes de Club Almacén.' },
  publicidad_tortas:     { title: 'Campaña software',     body: 'Incluir en el próximo envío dirigido a quienes van a comprar un software.' },
  publicidad_diamantes:  { title: 'Campaña Interesados',  body: 'Incluir en el próximo envío dirigido al segmento interesado (diamantes).' },
  publicidad_bombillas:  { title: 'Campaña por atención', body: 'Incluir en el próximo envío dirigido al segmento por estado de atención.' },
  publicidad_sercotec:   { title: 'Campaña SERCOTEC',     body: 'Incluir en el próximo envío dirigido a contactos SERCOTEC.' },
  publicidad_frio:       { title: 'Campaña re-enganche',  body: 'Incluir en el próximo envío de re-enganche a diamantes que se enfriaron.' },

  // Canales de contacto (sin pill)
  phone:    { title: 'Volver a llamar',             body: 'Hay que llamar para ver si hay interés.' },
  mobile:   { title: 'Problemas de celular',        body: 'Contacto disponible solo por WhatsApp o correo.' },
  whatsapp: { title: 'Contactar solo por WhatsApp', body: 'Prefiere o solo responde por WhatsApp.' },
  email:    { title: 'Contactar solo por correo',   body: 'Prefiere o solo responde por correo electrónico.' },

  // Estado de atención (sin pill)
  bulb_on:  { title: 'Se iniciaron acciones', body: 'Ya alguien lo está atendiendo.' },
  bulb_off: { title: 'Nadie lo ha atendido',  body: 'Sin atención aún. Asignar a alguien o atender.' },
}

/**
 * ICON_GROUPS — Orden visual del modal (top → bottom). Mismo orden que ICON_ROWS
 * en RatingIcon.jsx, pero agrupado con metadata de pill cuando aplica.
 */
export const ICON_GROUPS = [
  { key: 'cliente',         label: null,                       color: null,     icons: ['money'] },
  { key: 'compra_ca',       label: 'Compra en Club Almacén',   color: 'green',  icons: ['soccer', 'basketball', 'rugby', 'badminton'] },
  { key: 'sercotec',        label: 'SERCOTEC',                 color: 'amber',  icons: ['sercotec_plus', 'sercotec'] },
  { key: 'compra_software', label: 'Va a comprar software',    color: 'yellow', icons: ['cake_full', 'cake', 'coffee'] },
  { key: 'interesado',      label: 'Interesado',               color: 'red',    icons: ['diamond_alarm', 'diamond_advisor', 'diamond_return', 'diamond_plus', 'diamond_info', 'diamond_price', 'diamond'] },
  { key: 'frio',            label: null,                       color: null,     icons: ['diamond_blue', 'stopwatch', 'hourglass', 'cloud_rain', 'cloud_heavy', 'cloud_storm', 'rejected'] },
  { key: 'marketing',       label: 'Marketing / Campañas',     color: 'orange', icons: ['publicidad_software', 'publicidad_kits', 'publicidad_bole', 'publicidad_presupuesto', 'publicidad_pelotas', 'publicidad_sercotec', 'publicidad_tortas', 'publicidad_diamantes', 'publicidad_frio', 'publicidad_bombillas'] },
  { key: 'contacto',        label: null,                       color: null,     icons: ['phone', 'mobile', 'whatsapp', 'email'] },
  { key: 'atencion',        label: null,                       color: null,     icons: ['bulb_on', 'bulb_off'] },
]

/**
 * PILL_COLORS — paleta light + dark para los 5 colores de pill.
 * Aplicar via PILL_COLORS[group.color] cuando group.label !== null.
 */
export const PILL_COLORS = {
  green: {
    container: 'bg-green-50 border-green-200 dark:bg-green-900/15 dark:border-green-800/40',
    label:     'text-green-700 dark:text-green-300',
  },
  amber: {
    container: 'bg-amber-50 border-amber-200 dark:bg-amber-900/15 dark:border-amber-800/40',
    label:     'text-amber-700 dark:text-amber-300',
  },
  yellow: {
    container: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/15 dark:border-yellow-800/40',
    label:     'text-yellow-700 dark:text-yellow-300',
  },
  red: {
    container: 'bg-red-50 border-red-200 dark:bg-red-900/15 dark:border-red-800/40',
    label:     'text-red-700 dark:text-red-300',
  },
  orange: {
    container: 'bg-orange-50 border-orange-200 dark:bg-orange-900/15 dark:border-orange-800/40',
    label:     'text-orange-700 dark:text-orange-300',
  },
}

// Invariante de integridad (development only — Vite tree-shakes en producción)
if (import.meta.env.DEV && typeof console !== 'undefined' && console.assert) {
  const descKeys = new Set(Object.keys(ICON_DESCRIPTIONS))
  const groupIcons = new Set(ICON_GROUPS.flatMap(g => g.icons))
  const missingInGroups  = [...descKeys].filter(k => !groupIcons.has(k))
  const missingInDescs   = [...groupIcons].filter(k => !descKeys.has(k))
  console.assert(missingInGroups.length === 0,  'iconDescriptions: keys en ICON_DESCRIPTIONS no presentes en ICON_GROUPS:', missingInGroups)
  console.assert(missingInDescs.length === 0,   'iconDescriptions: iconos en ICON_GROUPS no presentes en ICON_DESCRIPTIONS:', missingInDescs)
}
